// src/components/pages/admin-dash/AdminMatchResultHitDistribution.jsx
import React, { useState } from "react";
import axiosInstance from "../../../utils/AxiosInstance";
import Card from "../../ui/Card";
import Spinner from "../../ui/Spinner";
import AdminMatchResultFilterBar from "./AdminMatchResultFilterBar";
import ReportBarList from "./ReportBarList";

/**
 * Admin report — how many match results land at each hit count (0-5),
 * with total prize per bucket (GET /admin/match-result/hit-distribution).
 * Roadmap item 3.4 — the one report that reads better as a bar chart
 * than a table, per the dataviz skill's form heuristic (magnitude
 * across a small ordinal category).
 */
const fmtUsd = (n) =>
  typeof n === "number"
    ? new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 }).format(n)
    : "$0";

export default function AdminMatchResultHitDistribution() {
  const [typeLottery, setTypeLottery] = useState("");
  const [parameter, setParameter] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [didSearch, setDidSearch] = useState(false);

  const fetchDistribution = async () => {
    setLoading(true);
    setError("");
    try {
      const params = {};
      if (typeLottery) params.typeLottery = typeLottery;
      if (parameter) params.parameter = parameter;
      if (startDate) params.startDate = startDate;
      if (endDate) params.endDate = endDate;
      const res = await axiosInstance.get("/admin/match-result/hit-distribution", { params });
      setRows(Array.isArray(res.data) ? res.data : []);
      setDidSearch(true);
    } catch {
      setError("Failed to load hit distribution.");
      setRows([]);
    } finally {
      setLoading(false);
    }
  };

  const handleReset = () => {
    setTypeLottery(""); setParameter(""); setStartDate(""); setEndDate("");
    setRows([]); setDidSearch(false); setError("");
  };

  const bars = rows.map((r) => ({
    key: r.hits,
    label: `${r.hits} hit${r.hits === 1 ? "" : "s"}`,
    count: r.totalLines || 0,
    secondaryValue: r.totalPrizeUsd || 0,
  }));

  return (
    <div className="space-y-4">
      <header>
        <h1 className="font-semibold mb-1" style={{ fontSize: 22, color: "var(--txt)" }}>
          Hit distribution
        </h1>
        <p className="text-[12px]" style={{ color: "var(--txt-2)" }}>
          How many generated lines land at each hit count (0–5 main numbers matched), and
          the total prize collected at each level.
        </p>
      </header>

      <AdminMatchResultFilterBar
        typeLottery={typeLottery} setTypeLottery={setTypeLottery}
        parameter={parameter} setParameter={setParameter}
        startDate={startDate} setStartDate={setStartDate}
        endDate={endDate} setEndDate={setEndDate}
        onSearch={fetchDistribution}
        onReset={handleReset}
        loading={loading}
      />

      {error && (
        <div
          className="rounded-xl px-4 py-3 text-[13px]"
          style={{ background: "var(--red-dim)", border: "1px solid var(--red-border)", color: "var(--red)" }}
        >
          {error}
        </div>
      )}

      <Card>
        {loading ? (
          <div className="flex items-center justify-center py-10">
            <Spinner size={18} />
          </div>
        ) : (
          <ReportBarList
            bars={bars}
            valueFormatter={(n) => `${n.toLocaleString()} line${n === 1 ? "" : "s"}`}
            secondaryFormatter={fmtUsd}
            emptyMessage={didSearch ? "No match results for this filter." : "Search to see the distribution."}
          />
        )}
      </Card>
    </div>
  );
}
